<template>
  <div id="appVersion">

    <PageTitle :title="$t('app_versions.title')" :subtitle="$t('app_versions.description')">
      <div class="generation-form">
        <div class="group">
          <label>{{$t('app_versions.choose_app')}}</label>
          <select v-model="selectedApp">
            <option :value="null" disabled hidden>{{$t('choose')}}...</option>
            <option v-for="app of apps" :value="app" :key="`app_${app.name}`">{{app.name}}</option>
          </select>
        </div>
        <div class="group" v-if="platforms">
          <label>{{$t('app_versions.choose_platform')}}</label>
          <select v-model="selectedPlatform">
            <option :value="null" disabled hidden>{{$t('choose')}}...</option>
            <option v-for="platform of platforms" :value="platform" :key="`plf_${platform}`">{{platform}}</option>
          </select>
        </div>
        <button class="btn btn-primary" @click="setToModify()" v-if="areAppAndPlatformSelected">{{$t('app_versions.create')}}</button>
      </div>
    </PageTitle>

    <PageContainer>
      <Empty :text="$t('app_versions.empty')" v-if="historyOfSelectedAppAndPlatform.length === 0"></Empty>
      <table class="table" v-if="historyOfSelectedAppAndPlatform.length > 0">
        <tr>
          <th class="version">{{$t('app_versions.version')}}</th>
          <th class="release-date">{{$t('app_versions.release_date')}}</th>
          <th class="description">{{$t('app_versions.version_description')}}</th>
          <th class="action"></th>
        </tr>
        <tr v-for="(entry, entryIndex) of historyOfSelectedAppAndPlatform" :key="'entry-' + entryIndex">
          <td class="version">{{entry.version}}</td>
          <td class="release-date">{{prettyDate(entry.releaseDate)}}</td>
          <td class="description">
            <div class="entry-description">
              <template v-if="entry.description.new && $to(entry.description.new)">
                <div class="entry-title">{{$t('app_versions.new')}}</div>
                <div class="entry-content">{{$to(entry.description.new)}}</div>
              </template>
              <template v-if="entry.description.improved && $to(entry.description.improved)">
                <div class="entry-title">{{$t('app_versions.improved')}}</div>
                <div class="entry-content">{{$to(entry.description.improved)}}</div>
              </template>
              <template v-if="entry.description.fixed && $to(entry.description.fixed)">
                <div class="entry-title">{{$t('app_versions.fixed')}}</div>
                <div class="entry-content">{{$to(entry.description.fixed)}}</div>
              </template>
            </div>
          </td>
          <td class="menu-items">
            <div class="actions">
              <ActionMenu icon="/icon/actions-horizontal.png">
                <div class="item" @click="setToModify(entry)">{{$t('edit')}}</div>
                <div class="item" @click="setToDelete(entry)">{{$t('delete')}}</div>
              </ActionMenu>
            </div>
          </td>
        </tr>
      </table>
    </PageContainer>

    <Modal name="modify-entry" class="modify-modal" :title="$t('app_versions.release')"
           :actions="[{action: clearEntryToModify, name: $t('back')},
                      {action: saveEntryToModify, name: $t('save'), primary: true}]">
      <template v-if="entryToModify">
        <div class="columns">
          <div class="column group">
            <label>{{$t('app_versions.version')}}</label>
            <input type="text" v-model="entryToModify.version">
          </div>
          <div class="column group">
            <label>{{$t('app_versions.release_date')}}</label>
            <datetime
              type="date"
              v-model="entryToModifyReleaseDateProxy"
              value-zone="local"
              input-id="entry_release_date"
              input-style="width: 100%"
            />
          </div>
        </div>
        <LocalizedInput textarea :translate="entryToModify.description.new" :title="$t('app_versions.new')"/>
        <LocalizedInput textarea :translate="entryToModify.description.improved" :title="$t('app_versions.improved')"/>
        <LocalizedInput textarea :translate="entryToModify.description.fixed" :title="$t('app_versions.fixed')"/>
      </template>
    </Modal>

  </div>
</template>

<script>
import {Core} from "@/core/Core";
import {DateTime} from "luxon";
import LocalizedInput from "@/components/tools/LocalizedInput";
import PageTitle from "@/components/PageTitle";
import PageFooter from "@/components/PageFooter";
import PageContainer from "@/components/PageContainer";
import ActionMenu from "@/components/tools/ActionMenu.vue";
import Empty from "@/components/tools/Empty";

export default {
  name: "AppVersion",

  components: { LocalizedInput, PageTitle, PageContainer, PageFooter, ActionMenu, Empty },

  data() {
    return {
      selectedApp: null,
      selectedPlatform: null,
      historyOfSelectedAppByPlatform: null,
      entryToModify: null,

      /* possibly transfers this data to the api */
      apps: [
        {
          name: 'pos',
          platforms: ['sunmi', 'pax', 'play_store']
        },
        {
          name: 'admin',
          platforms: ['web','play_store', 'node']
        }
      ]
    }
  },

  computed: {
    entryToModifyReleaseDateProxy: {
      get() { return this.entryToModify ? this.toIsoTime(this.entryToModify.releaseDate) : null },
      set(value) { this.entryToModify.releaseDate = this.fromIsoTime(value) }
    },
    selectedAppName() {
      return this.selectedApp ? this.selectedApp.name : null;
    },
    platforms() {
      return this.selectedApp ? this.selectedApp.platforms : null;
    },
    areAppAndPlatformSelected() {
      return !!this.selectedApp && !!this.selectedPlatform;
    },
    latestVersionEntry() {
      return this.historyOfSelectedAppAndPlatform ? (this.historyOfSelectedAppAndPlatform[0] || null) : null;
    },
    historyOfSelectedAppAndPlatform() {
      return this.selectedApp && this.selectedPlatform && this.historyOfSelectedAppByPlatform
        ? (this.historyOfSelectedAppByPlatform[this.selectedPlatform] || [])
        : [];
    }
  },

  watch: {
    async selectedApp(val) {
      if (val) {
        await this.loadHistoryOfApp(val.name);
      }
    }
  },

  mounted() {
    this.$store.state.pageTitleTranslationKey = "app_versions.title";
    this.$store.state.pageIcon = "/icon/settings.svg";
  },

  methods: {
    setToModify(entry) {
      this.entryToModify = entry
        ? JSON.parse(JSON.stringify(entry))
        : {
          name: this.selectedAppName,
          platform: this.selectedPlatform,
          version: this.latestVersionEntry ? this.nextVersionOf(this.latestVersionEntry.version) : '1.0.0',
          description: {
            new: {fr: '', en: ''},
            improved: {fr: '', en: ''},
            fixed: {fr: '', en: ''}
          },
          releaseDate: null
        };
      this.$modal.show("modify-entry");
    },

    clearEntryToModify() {
      this.entryToModify = null;
      this.$modal.show("modify-entry", false);
    },

    prettyDate(str) {
      if (!str) {
        return '';
      }
      let luxonDate = DateTime.fromFormat(str, 'yyyy-MM-dd');
      return luxonDate.invalid ? str : luxonDate.toLocaleString(DateTime.DATE_MED);
    },

    fromIsoTime(isoTime) {
      return isoTime ? DateTime.fromISO(isoTime).toFormat('yyyy-MM-dd') : null
    },

    toIsoTime(crudeTime) {
      return crudeTime ? DateTime.fromFormat(crudeTime, 'yyyy-MM-dd').toISO() : null
    },

    nextVersionOf(version) {
      if (typeof version !== 'string') {
        return '';
      }
      let split = version.split('.');
      let lastPart = parseInt(split[split.length-1]);
      if (isNaN(lastPart)) {
        return '';
      }
      split[split.length-1] = `${lastPart + 1}`;
      return split.join('.');
    },

    async loadHistoryOfApp(appName) {
      let response = await Core.noThrowRequest('GET', `/api/app_versions/${appName}/history`);
      if (response) {
        this.historyOfSelectedAppByPlatform = response.platform;
      }
    },

    async saveEntryToModify() {
      let entry = this.entryToModify;
      if (!entry) {
        return;
      }
      showSpinner();
      let response = entry.id
          ? await Core.noThrowRequest('PUT', `/api/app_versions/${entry.id}`, entry)
          : await Core.noThrowRequest('POST', `/api/app_versions`, entry) ;
      if (response) {
        await this.loadHistoryOfApp(entry.name);
        this.clearEntryToModify();
      }
      hideSpinner();
    },

    async setToDelete(entry) {
      let warningTitle = this.$t('warning');
      let warningContent = this.$t('app_versions.delete_confirmation')
        .replace('%APP_NAME%', entry.name)
        .replace('%VERSION%', entry.version)
        .replace('%PLATFORM%', entry.platform);

      let deleteConfirmed = await showConfirmation(warningTitle, warningContent);
      if (deleteConfirmed) {
        await this.deleteEntry(entry);
      }
    },

    async deleteEntry(entry) {
      showSpinner();
      let response = await Core.noThrowRequest('DELETE', `/api/app_versions/${entry.id}?platform=${entry.platform}&version=${entry.version}&name=${entry.name}`);
      if (response) {
        await this.loadHistoryOfApp(entry.name);
      }
      hideSpinner();
    }
  }
}
</script>

<style lang="scss" scoped>
  #appVersion {
    display: flex;
    flex-direction: column;
  }
  .table {
    width: 100%;

    tr {
      &:not(:last-child) {
        td, th {
          border-bottom: 1px solid #e6e6e6;
        }
      }
    }

    td, th {
      &.version {
        font-weight: bold;
        text-align: center;
        padding-right: 10px;
      }
      &.release-date {
        text-align: center;
        padding-right: 20px;
      }
      &.description {
        white-space: pre-wrap;
      }
    }

    th {
      text-align: left;
      font-size: 14px;
      padding-bottom: 16px;
    }

    td {
      padding-top: 16px;
      padding-bottom: 16px;
      &:first-child {
        padding-left: 16px;
      }
      &:last-child {
        padding-right: 16px;
      }
    }
  }
  .entry-description {
    font-size: 14px;
    .entry-title {
      font-weight: bold;
      margin-bottom: 5px;
    }
    .entry-content {
      &:not(:last-child) {
        padding-bottom: 10px;
      }
    }
  }
  .actions {
    height: 32px;
    width: 100%;
  }
  .generation-form {
    display: flex;
    justify-content: center;
    align-items: center;

    .group {
      margin-right: 10px;
      margin-bottom: 0;
    }
    .btn {
      margin-top: 15px;
    }
  }
</style>

<style lang="scss">
  #appVersion {
    .modify-modal {
      .modalContainer {
        width: 600px;

        label {
          margin-bottom: 5px;
        }

        textarea {
          min-height: 10em;
          margin-bottom: 10px;
        }
      }
    }
  }
</style>